import { useEffect, useState } from "react";
import { subUserAPI } from "../api/subUsers";
import { STORAGE_KEYS } from "../constants/storageKeys";
import { safeGetJSON } from "../utils/safeStorage";

/**
 * "What can the currently logged-in identity do" — resolved server-side, so the
 * owner always gets everything and a sub-user gets their granted set intersected
 * with the owner's own account-level toggles.
 *
 * Cached at module level: several screens ask for this on mount and the answer
 * can't change within a session without a reload (permissions are set by the
 * owner in a different session), so one request per page load is enough. The
 * cache is dropped on logout via clearAuthData(), so it never outlives the identity
 * it was fetched for.
 *
 * Backend enforcement is the real gate — this only drives UI affordances. Until a
 * permission set has actually been resolved (still loading, or the fetch failed):
 *   - an OWNER is allowed everything, so a transient network error never locks the
 *     owner out of their own app;
 *   - a SUB-USER is denied everything, so denied editing UI never flashes on screen
 *     while loading and doesn't reappear if the fetch fails.
 * Whether this is a sub-user session is known synchronously from the cached login
 * payload, which is what makes the second rule possible before the fetch resolves.
 */
let cachedPromise = null;

export function clearMyPermissionsCache() {
  cachedPromise = null;
}

function fetchMyPermissions() {
  if (!cachedPromise) {
    cachedPromise = subUserAPI.getMyPermissions().catch((err) => {
      cachedPromise = null;
      throw err;
    });
  }
  return cachedPromise;
}

function cachedLoginIsSubUser() {
  return Boolean(safeGetJSON(STORAGE_KEYS.user)?.is_sub_user);
}

export function useMyPermissions() {
  const [state, setState] = useState(() => ({
    loading: true,
    isSubUser: cachedLoginIsSubUser(),
    permissions: null,
    actorDisplayName: "",
  }));

  useEffect(() => {
    let cancelled = false;
    fetchMyPermissions()
      .then((data) => {
        if (cancelled) return;
        setState({
          loading: false,
          isSubUser: Boolean(data?.is_sub_user),
          permissions: data?.permissions || {},
          actorDisplayName: data?.actor_display_name || "",
        });
      })
      .catch(() => {
        if (cancelled) return;
        setState((prev) => ({ ...prev, loading: false, permissions: null }));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const can = (key) => {
    if (!state.permissions) return !state.isSubUser;
    return Boolean(state.permissions[key]);
  };

  /**
   * True if ANY write permission in a module is granted — e.g. canEditModule("marketplace")
   * is true for a sub-user holding only marketplace.delete. Use it to decide whether a
   * whole editing section / nav entry renders at all; use can(key) for individual
   * buttons inside it. `*.view` keys are read gates, not writes, so they never count.
   * `prefix` is the part before the dot in the registry key (see
   * backend/app/utils/sub_user_permissions.py).
   */
  const canEditModule = (prefix) => {
    if (!state.permissions) return !state.isSubUser;
    const start = `${prefix}.`;
    return Object.entries(state.permissions).some(
      ([key, enabled]) => enabled && key.startsWith(start) && !key.endsWith(".view")
    );
  };

  return { ...state, can, canEditModule };
}
